import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { dispatchApi as D, DISPATCH_STATUS_STYLE } from "@/lib/dispatch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Hand, Truck, PackageCheck, CheckCircle2, Lock, Save } from "lucide-react";

export default function DispatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [t, setT] = useState({ name: "", vehicle_no: "", driver_name: "", driver_phone: "", lr_no: "" });
  const canEdit = can("dispatch", "edit");

  const load = async () => {
    setLoading(true);
    try { const x = await D.get(id); setD(x); setT({ name: "", vehicle_no: "", driver_name: "", driver_phone: "", lr_no: "", ...(x.transporter || {}) }); }
    catch { toast.error("Not found"); navigate("/dispatch"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const move = async (status) => { try { setD(await D.setStatus(id, status)); toast.success(`Marked ${status}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const saveTransport = async () => { try { setD(await D.update(id, { transporter: t })); toast.success("Transport details saved"); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };

  if (loading || !d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  const editable = ["Planning", "Ready to Pick", "Picked", "Loaded"].includes(d.status);
  const totalMeters = (d.items || []).reduce((s, i) => s + (i.meters || 0), 0);

  return (
    <div className="space-y-6" data-testid="dispatch-detail-page">
      <button onClick={() => navigate("/dispatch")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back to dispatch</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{d.challan_no}</h2>
            <Badge className={`hover:bg-inherit ${DISPATCH_STATUS_STYLE[d.status]}`} data-testid="dsp-status-badge">{d.status}</Badge>
            {d.status === "Closed" && <Badge className="bg-slate-200 text-slate-500 hover:bg-slate-200"><Lock className="mr-1 h-3 w-3" />Immutable</Badge>}
          </div>
          <p className="mt-1 text-sm text-slate-500">{d.customer} · {d.so_no} · {d.items?.length} roll(s) · {totalMeters} m</p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {d.status === "Planning" && <Button className="bg-indigo-600 text-white hover:bg-indigo-700" onClick={() => move("Ready to Pick")} data-testid="ready-btn"><PackageCheck className="mr-2 h-4 w-4" />Ready to Pick</Button>}
            {d.status === "Ready to Pick" && <Button variant="outline" onClick={() => move("Picked")} data-testid="pick-btn"><Hand className="mr-2 h-4 w-4" />Confirm Picking</Button>}
            {d.status === "Picked" && <Button variant="outline" onClick={() => move("Loaded")} data-testid="load-btn"><Truck className="mr-2 h-4 w-4" />Confirm Loading</Button>}
            {d.status === "Loaded" && <Button className="bg-indigo-600 text-white hover:bg-indigo-700" onClick={() => move("Dispatched")} data-testid="dispatch-btn"><Truck className="mr-2 h-4 w-4" />Complete Dispatch</Button>}
            {d.status === "Dispatched" && <Button variant="outline" onClick={() => move("Delivered")} data-testid="delivered-btn"><CheckCircle2 className="mr-2 h-4 w-4" />Mark Delivered</Button>}
            {["Dispatched", "Delivered"].includes(d.status) && <Button variant="outline" onClick={() => move("Closed")} data-testid="close-btn"><Lock className="mr-2 h-4 w-4" />Close</Button>}
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-heading text-base font-semibold text-slate-900">Rolls / packages</h3>
          <div className="mt-4 overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">Item</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Roll / Package</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Meters</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Grade</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(d.items || []).map((it) => (
                  <TableRow key={it.item_id} data-testid={`challan-item-${it.item_no}`}>
                    <TableCell className="font-medium text-slate-800">{it.item_no}</TableCell>
                    <TableCell className="text-slate-700">{it.batch_id}</TableCell>
                    <TableCell className="tabular-nums text-slate-700">{it.meters} m</TableCell>
                    <TableCell className="text-slate-600">{it.qc_grade || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="font-heading text-base font-semibold text-slate-900">Ship to</h3>
            <p className="mt-2 text-sm font-medium text-slate-800">{d.customer}</p>
            <p className="text-sm text-slate-500">{[d.shipping_address?.line1, d.shipping_address?.city, d.shipping_address?.state, d.shipping_address?.pincode].filter(Boolean).join(", ") || "—"}</p>
          </Card>
          <Card className="p-6">
            <h3 className="font-heading text-base font-semibold text-slate-900">Transport</h3>
            <div className="mt-3 space-y-2">
              <div className="space-y-1"><Label className="text-xs">Transporter *</Label><Input value={t.name} disabled={!editable || !canEdit} onChange={(e) => setT({ ...t, name: e.target.value })} data-testid="transporter-name-input" /></div>
              <div className="space-y-1"><Label className="text-xs">Vehicle no *</Label><Input value={t.vehicle_no} disabled={!editable || !canEdit} onChange={(e) => setT({ ...t, vehicle_no: e.target.value })} data-testid="vehicle-no-input" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1"><Label className="text-xs">Driver</Label><Input value={t.driver_name} disabled={!editable || !canEdit} onChange={(e) => setT({ ...t, driver_name: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Driver phone</Label><Input value={t.driver_phone} disabled={!editable || !canEdit} onChange={(e) => setT({ ...t, driver_phone: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">LR / Consignment no</Label><Input value={t.lr_no} disabled={!editable || !canEdit} onChange={(e) => setT({ ...t, lr_no: e.target.value })} data-testid="lr-no-input" /></div>
              {editable && canEdit && <Button size="sm" variant="outline" onClick={saveTransport} data-testid="save-transport-btn" className="mt-1"><Save className="mr-2 h-3.5 w-3.5" />Save transport</Button>}
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="font-heading text-base font-semibold text-slate-900">History</h3>
        <div className="mt-4 space-y-3">
          {[...(d.history || [])].reverse().map((h, i) => (
            <div key={i} className="flex gap-3 border-b border-slate-100 pb-3" data-testid="dsp-history-item">
              <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-indigo-400" />
              <div><p className="text-sm text-slate-700"><span className="font-medium">{h.status}</span>{h.note ? ` · ${h.note}` : ""}</p><p className="text-xs text-slate-400">{h.by} · {new Date(h.at).toLocaleString()}</p></div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
